// next-app/src/store/survey-store.ts
import { create } from 'zustand';
import type { SurveyConfig, SurveyResponse } from '@/types';

// ============================================================================
// Types
// ============================================================================

export interface SurveyState {
  config: SurveyConfig | null;
  formData: Partial<SurveyResponse>;
  selectedVehicles: number[];
  step: number;
  isSubmitting: boolean;
  error: string | null;
}

interface SurveyActions {
  setConfig: (config: SurveyConfig) => void;
  setFormData: (data: Partial<SurveyResponse>) => void;
  updateFormField: (field: string, value: unknown) => void;
  setSelectedVehicles: (vehicles: number[]) => void;
  toggleVehicle: (vehicleId: number) => void;
  setStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  setSubmitting: (isSubmitting: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

type SurveyStore = SurveyState & SurveyActions;

// ============================================================================
// Initial State
// ============================================================================

const initialState: SurveyState = {
  config: null,
  formData: {},
  selectedVehicles: [],
  step: 0,
  isSubmitting: false,
  error: null,
};

// ============================================================================
// Store Implementation
// ============================================================================

export const useSurveyStore = create<SurveyStore>()((set) => ({
  ...initialState,

  setConfig: (config) => set({ config }),

  setFormData: (data) => set({ formData: data }),

  updateFormField: (field, value) =>
    set((state) => ({
      formData: { ...state.formData, [field]: value },
    })),

  setSelectedVehicles: (vehicles) => set({ selectedVehicles: vehicles }),

  toggleVehicle: (vehicleId) =>
    set((state) => ({
      selectedVehicles: state.selectedVehicles.includes(vehicleId)
        ? state.selectedVehicles.filter((id) => id !== vehicleId)
        : [...state.selectedVehicles, vehicleId],
    })),

  setStep: (step) => set({ step }),

  nextStep: () => set((state) => ({ step: state.step + 1 })),

  prevStep: () => set((state) => ({ step: Math.max(0, state.step - 1) })),

  setSubmitting: (isSubmitting) => set({ isSubmitting }),

  setError: (error) => set({ error }),

  reset: () => set(initialState),
}));

// ============================================================================
// Selectors
// ============================================================================

export const selectSurveyConfig = (state: SurveyStore) => state.config;
export const selectSurveyFormData = (state: SurveyStore) => state.formData;
export const selectSelectedVehicles = (state: SurveyStore) => state.selectedVehicles;
export const selectSurveyStep = (state: SurveyStore) => state.step;
export const selectIsSubmitting = (state: SurveyStore) => state.isSubmitting;
export const selectSurveyError = (state: SurveyStore) => state.error;
